import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CONTENT_FIELDS = [
  "intro", "tagline", "hero_image", "lifestyle",
  "average_price_apartment", "rental_yield", "latitude", "longitude",
];

function completeness(row) {
  // Simple 0-100% score across the 8 fields that most influence how the
  // public /areas/<slug> page renders. Higher = more ready to publish.
  const filled = CONTENT_FIELDS.filter((k) => {
    const v = row[k];
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  }).length;
  return { pct: Math.round((filled / CONTENT_FIELDS.length) * 100), filled, total: CONTENT_FIELDS.length };
}

export default function AdminCommunities() {
  const [items, setItems] = useState([]);
  const load = () => { api.get("/communities?admin_view=true").then((r) => setItems(r.data.items || [])); };
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete community "${slug}"?`)) return;
    await api.delete(`/admin/communities/${slug}`);
    toast.success("Community deleted");
    load();
  };

  const translateAll = async () => {
    if (!window.confirm("Translate every community into all 6 languages using Emergent LLM? This can take a minute.")) return;
    toast.info("Starting bulk translation…");
    try {
      const { data } = await api.post("/admin/translate/bulk?entity=community&langs=ar,ru,zh,fr,ja,ko&limit=50");
      toast.success(`Translated ${data.processed} communities into ${data.langs.length} languages`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk translation failed");
    }
  };

  const recomputeMetrics = async () => {
    toast.info("Recomputing yield & investment score from live listings…");
    try {
      const { data } = await api.post("/admin/communities/recompute-metrics");
      toast.success(`Refreshed ${data.updated} communities`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    }
  };

  return (
    <div className="p-10" data-testid="admin-communities">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">UAE database</div>
          <h1 className="font-serif text-4xl">Communities</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={recomputeMetrics}
            data-testid="community-recompute-metrics"
            className="px-5 py-3 border border-[#001588] text-[#001588] text-sm uppercase tracking-[0.2em] hover:bg-[#001588] hover:text-white transition-colors"
          >
            Refresh metrics
          </button>
          <button
            onClick={translateAll}
            data-testid="community-translate-all"
            className="px-5 py-3 border border-[#001588] text-[#001588] text-sm uppercase tracking-[0.2em] hover:bg-[#001588] hover:text-white transition-colors"
          >
            Translate all
          </button>
          <Link
            to="/admin/communities/new"
            data-testid="admin-community-new-btn"
            className="px-5 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black transition-colors"
          >
            + New community
          </Link>
        </div>
      </div>

      <div className="border border-[#E5E7EB] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-[#E5E7EB] text-left uppercase text-xs tracking-wider text-gray-500">
            <tr>
              <th className="px-4 py-3">Community</th>
              <th className="px-4 py-3">Emirate / City</th>
              <th className="px-4 py-3">Avg apt (AED/sqft)</th>
              <th className="px-4 py-3">Yield</th>
              <th className="px-4 py-3">Investment</th>
              <th className="px-4 py-3">Publish</th>
              <th className="px-4 py-3">Content</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {items.map((a) => {
              const c = completeness(a);
              const barColor = c.pct >= 75 ? "bg-emerald-500" : c.pct >= 40 ? "bg-amber-500" : "bg-red-500";
              return (
              <tr key={a.slug} data-testid={`community-row-${a.slug}`}>
                <td className="px-4 py-3 font-medium">{a.name}</td>
                <td className="px-4 py-3 text-gray-600">{a.emirate || "Dubai"} · {a.city || "Dubai"}</td>
                <td className="px-4 py-3">{a.average_price_apartment || "—"}</td>
                <td className="px-4 py-3">{a.rental_yield ? `${a.rental_yield}%` : "—"}</td>
                <td className="px-4 py-3">{a.investment_score ? `${a.investment_score}/10` : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 border ${
                    (a.publish_status === "published" || a.published) ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                    a.publish_status === "review" ? "bg-amber-50 border-amber-200 text-amber-800" :
                    "bg-gray-50 border-gray-200 text-gray-700"
                  }`}>
                    {a.publish_status || (a.published ? "published" : "draft")}
                  </span>
                </td>
                <td className="px-4 py-3 min-w-[120px]">
                  <div className="flex items-center gap-2" title={`${c.filled} of ${c.total} key fields filled`} data-testid={`community-completeness-${a.slug}`}>
                    <div className="flex-1 h-1.5 bg-neutral-200 overflow-hidden">
                      <div className={`h-full ${barColor}`} style={{ width: `${c.pct}%` }} />
                    </div>
                    <span className="text-xs text-gray-600 tabular-nums w-9 text-right">{c.pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  <Link to={`/admin/communities/${a.slug}/edit`} data-testid={`community-edit-${a.slug}`} className="text-[#001588] hover:underline text-xs uppercase tracking-wider">Edit</Link>
                  <button onClick={() => remove(a.slug)} data-testid={`community-delete-${a.slug}`} className="text-red-600 hover:underline text-xs uppercase tracking-wider">Delete</button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
